import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { ref } from 'vue';
import { apiRequest } from './api-request';
import { appRouteTo } from '@/router';
import { UsersController } from './api-routes';
import { RouteLocationNamedRaw } from 'vue-router';
import { MessageType } from '@shared/firebase-cloud-messages';
import { UserType } from '@shared/users';

export const pushNotificationToken = ref<string | null>(null);

const addListeners = async () => {
  await PushNotifications.addListener('registration', async (token) => {
    localStorage.setItem('pushNotificationToken', token.value);
    pushNotificationToken.value = token.value;
    if (localStorage.getItem('token')) {
      // send the token to the server
      try {
        await apiRequest(UsersController.updateMessagingToken, {
          bodyParams: { messagingToken: token.value },
        });
      } catch (e) {
        console.error('Failed to send messaging token to server');
        console.error(e);
      }
    }
  });

  await PushNotifications.addListener('registrationError', (err) => {
    console.error('Registration error: ', err.error);
  });

  await PushNotifications.addListener(
    'pushNotificationReceived',
    (notification) => {
      // eslint-disable-next-line no-console
      console.log(
        'Push notification received: ',
        notification.data.messageType,
      );
    },
  );

  await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (event) => {
      const routeTable: Record<UserType, Record<MessageType, RouteLocationNamedRaw>> = {
        // DEFINE THE ROUTES FOR THE PUSH NOTIFICATIONS HERE
        Sitter: {
          notification: { name: 'sitter_notifications', params: {} },
          sitterJobSummary: { name: 'sitter_available_jobs', params: {} },
          newAdvertisement: { name: 'sitter_advertisement', params: {id: event.notification.data.target as string} },
        },
        AdminGroup: {
          notification: { name: 'admin_notifications', params: {} },
          sitterJobSummary: { name: "login" }, // only included to satisfy typescript
          newAdvertisement: { name: "login" }, // only included to satisfy typescript

        },
        Customer: {
          notification: { name: 'client_notifications', params: {} },
          sitterJobSummary: { name: "login" }, // only included to satisfy typescript
          newAdvertisement: { name: "login" }, // only included to satisfy typescript
        },
      };

      let route: RouteLocationNamedRaw | undefined = undefined;
      const userType = localStorage.getItem('userType') as UserType;
      if (userType) {
        route = routeTable[userType]?.[event.notification.data.messageType as MessageType];
      }

      if (route) {
        appRouteTo(route);
      } else {
        console.warn('Unknown push message type for', userType);
      }
    },
  );
};

export const registerPushNotifications = async (registerListeners: boolean) => {
  if (Capacitor.getPlatform() == 'web') {
    return;
  }

  // start with the stored version of the token, it will be updated
  // when a new token is registered.
  pushNotificationToken.value = localStorage.getItem('pushNotificationToken');

  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive === 'granted') {
    await PushNotifications.register();
    if (registerListeners) {
      await addListeners();
    }
  } else {
    console.warn('User denied push notification permissions!');
    throw new Error('User denied permissions!');
  }
};

export const getDeliveredPushNotifications = async () => {
  if (Capacitor.getPlatform() == 'web') {
    return [];
  }
  const notificationList = await PushNotifications.getDeliveredNotifications();
  return notificationList.notifications;
};

export const getNotificationToken = async () => {
  if (Capacitor.getPlatform() == 'web') {
    return;
  }
  return localStorage.getItem('pushNotificationToken');
};

// a simple wrapper that prevents errors on the web platform
export const addPushNotificationReceivedListener = async (
  callback: (data: any) => void,
) => {
  if (Capacitor.getPlatform() == 'web') {
    return;
  }
  return PushNotifications.addListener('pushNotificationReceived', callback);
};
